import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Upload, FileUp, Plus, X, MessageCircleQuestion } from 'lucide-react';
import { api } from '@/api/client';
import { lsGet, lsSet } from '@/lib/storage';
import { useAuth } from '@/state/auth';
import { toast } from '@/state/toast';
import { track } from '@/state/telemetry';
import { DashboardTopBar } from '@/components/DashboardTopBar';
import { ProjectHeader } from '@/components/ProjectHeader';
import { StatusBar, type StatusFilter } from '@/components/StatusBar';
import { ROLE_TABS, TabBar, type TabId } from '@/components/TabBar';
import { SubFilterPills, type RemarkFilter } from '@/components/SubFilterPills';
import { RemarkCard } from '@/components/RemarkCard';
import { RemarkDetail } from '@/components/RemarkDetail';
import { AIViewPanel } from '@/components/AIViewPanel';
import { Button } from '@/components/Button';
import { Spinner } from '@/components/Spinner';
import { EmptyState } from '@/components/EmptyState';
import { Modal } from '@/components/Modal';
import { CreateRemarkModal } from '@/components/modals/CreateRemarkModal';
import { CreateProjectModal } from '@/components/modals/CreateProjectModal';
import { InviteModal } from '@/components/modals/InviteModal';
import { WordImportModal } from '@/components/modals/WordImportModal';
import { DocxResponseImportModal } from '@/components/modals/DocxResponseImportModal';
import { DocumentsTab } from '@/components/DocumentsTab';
import { QueryTab } from '@/components/QueryTab';
import { IntegrityTab } from '@/components/IntegrityTab';
import { WavesView } from '@/components/IntegrityTab';
import { GIPSplitView } from '@/components/GIPSplitView';
import { PassportPanel } from '@/components/PassportPanel';
import { AIFullSwapView } from '@/components/AIFullSwapView';
import { CollegiumTab } from '@/components/CollegiumTab';
import { SettingsTab, ComparePanel } from '@/components/SettingsTab';
import { ProjectSettingsSheet, type SettingsSection } from '@/components/ProjectSettingsSheet';
import { PromoteFindingsModal } from '@/components/PromoteFindingsModal';
import { AskSpecialistModal } from '@/components/AskSpecialistModal';
import { ConsultationsListModal } from '@/components/ConsultationsListModal';
import { TriageModal } from '@/components/modals/TriageModal';
// AutoRoutingModal deprecated: backend endpoints /route/preview и /route/apply
// не существуют; функционал перенесён в DistributeDocumentsModal (распределение
// томов по экспертам — это более фундаментальная единица).
import { DistributeDocumentsModal } from '@/components/modals/DistributeDocumentsModal';
import { FileSearch, Sparkles as SparklesIcon } from 'lucide-react';
import type { Project, RemarksDashboardResponse, SnapshotsResponse } from '@/types/api';
import { normSeverity } from '@/types/api';
import { Inbox } from 'lucide-react';

type ModalKind = 'create-remark' | 'create-project' | 'invite' | 'word-import' | 'response-import' | 'triage' | 'distribute-docs' | null;

/** Главный экран проекта. Wire-up всех компонентов Sprint 2. */
export default function ProjectDashboard() {
  const { id: projectId = '' } = useParams<{ id: string }>();
  const nav = useNavigate();
  const activeRole = useAuth((s) => s.activeRole) || 'curator';
  const isCustomer = activeRole === 'customer';
  // Под-навигация внутри двух объединённых вкладок заказчика (2026-07-05).
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [askSpecOpen, setAskSpecOpen] = useState(false); // 15.C «Спросить специалиста»
  const [consultOpen, setConsultOpen] = useState(false); // 15.D список консультаций (иконка в шапке)
  // IA заказчика: лист «Настройки проекта» (проектировщик/тариф/паспорт). Открывается
  // ⚙ в шапке (секция project), чипом «Проектировщик» (секция designer) и пунктом меню
  // аккаунта. URL: ?ps=1 → секция project; ?dm=1 (back-compat 13.3) → секция designer.
  const [settingsSection, setSettingsSection] = useState<SettingsSection | null>(null);
  const [docsOpen, setDocsOpen] = useState(false); // 14.1: «Загрузка и редакции» → окно из чипа шапки
  // 13.4: у заказчика замечания — окно-слой, открывается кнопкой «+ Замечание приёмки»
  // (URL ?remarks=open переживает reload).
  const [remarksOverlayOpen, setRemarksOverlayOpen] = useState(false);

  // URL-state: ?tab=…&view=…&panel=… — переживает hard-reload.
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS: TabId[] = ['remarks', 'docs', 'query', 'integrity', 'collegium', 'compare', 'settings'];
  const initialTab: TabId = (() => {
    const t = searchParams.get('tab') as TabId | null;
    return t && VALID_TABS.includes(t) ? t : 'remarks';
  })();
  const [tab, setTabState] = useState<TabId>(initialTab);
  const setTab = (t: TabId) => {
    if (t !== tab) {
      track('tab_switched', { project_id: projectId, payload: { from: tab, to: t } });
    }
    setTabState(t);
    // Обновить URL без полной перезагрузки, сохранив сторонние query.
    const next = new URLSearchParams(searchParams);
    if (t === 'remarks') {
      next.delete('tab');
    } else {
      next.set('tab', t);
    }
    // При смене таба чистим зависимые query, чтобы не пересекались.
    if (t !== 'integrity') next.delete('view');
    if (t !== 'settings') next.delete('panel');
    setSearchParams(next, { replace: true });
  };
  // Подхватываем обратное направление: если URL изменили извне (back/forward),
  // синхронизируем локальный state.
  useEffect(() => {
    const t = searchParams.get('tab') as TabId | null;
    const next = t && VALID_TABS.includes(t) ? t : 'remarks';
    if (next !== tab) setTabState(next);
    // Лист настроек: ?ps=1 → секция project; ?dm=1 (back-compat 13.3) → секция designer.
    if (searchParams.get('ps') === '1') setSettingsSection('project');
    else if (searchParams.get('dm') === '1') setSettingsSection('designer');
    // 13.4: ?remarks=open → открыть окно замечаний.
    if (searchParams.get('remarks') === 'open') setRemarksOverlayOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const closeSettings = () => {
    setSettingsSection(null);
    if (searchParams.get('dm') || searchParams.get('ps')) {
      const n = new URLSearchParams(searchParams); n.delete('dm'); n.delete('ps'); setSearchParams(n, { replace: true });
    }
  };
  const openRemarksOverlay = () => {
    setRemarksOverlayOpen(true);
    const n = new URLSearchParams(searchParams); n.set('remarks', 'open'); setSearchParams(n, { replace: true });
  };
  const closeRemarksOverlay = () => {
    setRemarksOverlayOpen(false);
    if (searchParams.get('remarks')) { const n = new URLSearchParams(searchParams); n.delete('remarks'); setSearchParams(n, { replace: true }); }
  };

  // При смене роли — сброс на 'remarks', если активный таб больше не виден.
  useEffect(() => {
    const allowed = ROLE_TABS[activeRole as keyof typeof ROLE_TABS] || ROLE_TABS.curator;
    if (!allowed.includes(tab)) setTab(allowed[0] || 'remarks');
  }, [activeRole, tab]);
  // Префильтр «Мои назначения»: персистим последний выбор фильтра в localStorage
  // под ключом по email пользователя (см. red-items, доп. инструкция).
  const userEmail = useAuth((s) => s.user?.email);
  // Phase 2B: ключ без префикса — lsGet/lsSet добавит collegium./consolida. сами.
  const LS_FILTER_KEY = userEmail ? `remarkFilter:${userEmail}` : null;
  const [filter, setFilter] = useState<RemarkFilter>(() => {
    if (!LS_FILTER_KEY) return 'all';
    const stored = lsGet(LS_FILTER_KEY);
    if (stored === 'all' || stored === 'open' || stored === 'closed' || stored === 's' || stored === 'e' || stored === 'archived') {
      return stored as RemarkFilter;
    }
    return 'all';
  });
  useEffect(() => {
    if (!LS_FILTER_KEY) return;
    lsSet(LS_FILTER_KEY, filter);
  }, [filter, LS_FILTER_KEY]);
  const [selectedRemarkId, setSelectedRemarkId] = useState<string | null>(null);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiSwap, setAiSwap] = useState(false);
  const [passportOpen, setPassportOpen] = useState(false);
  const [modal, setModal] = useState<ModalKind>(null);

  const projectQ = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
    enabled: !!projectId,
  });
  const snapshotsQ = useQuery({
    queryKey: ['snapshots', projectId],
    queryFn: () => api.get<SnapshotsResponse>(`/projects/${projectId}/snapshots`),
    enabled: !!projectId,
  });
  // Когда чип «Показать архив» активен — просим include_archived=true. Иначе
  // дефолт сервера false (см. PATCH /projects/{pid}/remarks/{rid} red-item).
  const includeArchived = filter === 'archived';
  const remarksQ = useQuery({
    queryKey: ['remarks', projectId, { includeArchived }],
    queryFn: () => api.get<RemarksDashboardResponse>(
      `/projects/${projectId}/remarks/dashboard?include_archived=${includeArchived ? 'true' : 'false'}`,
    ),
    enabled: !!projectId,
  });

  // Сводка авто-анализа для верхних бейджей «Решение»/«Комплектность»
  // (те же данные, что на вкладке «Готовность» — общий queryKey дедуплицирует).
  const readinessQ = useQuery<{ completeness?: { has_result?: boolean; absent?: number }; decisions?: { has_result?: boolean; count?: number } }>({
    queryKey: ['readiness-summary', projectId],
    queryFn: () => api.get(`/projects/${projectId}/readiness-summary`),
    enabled: !!projectId,
    staleTime: 30_000,
  });
  // Вариант A: дубль-вход в сценарий B на вкладке «Замечания» (заказчик).
  const promotableQ = useQuery<{ hard_total: number; soft_total: number }>({
    queryKey: ['promotable-findings', projectId],
    queryFn: () => api.get(`/projects/${projectId}/promotable-findings`),
    enabled: !!projectId && activeRole === 'customer',
    staleTime: 30_000,
  });
  const promotableTotal = (promotableQ.data?.hard_total ?? 0) + (promotableQ.data?.soft_total ?? 0);

  const project = projectQ.data;
  const snapshots = snapshotsQ.data?.snapshots || [];
  const activeSnapshot = snapshots[0]; // самый свежий
  const remarks = remarksQ.data?.remarks || [];
  const summary = remarksQ.data?.summary;

  const counts = useMemo(() => {
    // archived в общем списке не приходит (include_archived=false), но фильтр чипа
    // переключает запрос на include_archived=true — тогда archived заполняется.
    const nonArchived = remarks.filter((r) => r.status !== 'archived');
    const all = nonArchived.length;
    const open = nonArchived.filter((r) => (r.remark_status || (r.closed_at ? 'closed' : 'open')) !== 'closed').length;
    const closed = nonArchived.filter((r) => (r.remark_status || (r.closed_at ? 'closed' : 'open')) === 'closed').length;
    const s = nonArchived.filter((r) => r.defect_type === 'S-defect').length;
    const e = nonArchived.filter((r) => r.defect_type === 'E-gap').length;
    const archived = remarks.filter((r) => r.status === 'archived').length;
    return { all, open, closed, s, e, archived };
  }, [remarks]);

  const filtered = useMemo(() => {
    let arr = remarks;
    if (filter === 'archived') {
      arr = arr.filter((r) => r.status === 'archived');
    } else {
      // во всех других фильтрах исключаем архив (даже если бэк прислал)
      arr = arr.filter((r) => r.status !== 'archived');
      if (filter === 'open')   arr = arr.filter((r) => (r.remark_status || (r.closed_at ? 'closed' : 'open')) !== 'closed');
      if (filter === 'closed') arr = arr.filter((r) => (r.remark_status || (r.closed_at ? 'closed' : 'open')) === 'closed');
      if (filter === 's')      arr = arr.filter((r) => r.defect_type === 'S-defect');
      if (filter === 'e')      arr = arr.filter((r) => r.defect_type === 'E-gap');
    }
    // Сортировка: открытые → закрытые, severity desc, created_at desc
    const sevWeight: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
    return [...arr].sort((a, b) => {
      const aOpen = (a.remark_status || (a.closed_at ? 'closed' : 'open')) !== 'closed' ? 0 : 1;
      const bOpen = (b.remark_status || (b.closed_at ? 'closed' : 'open')) !== 'closed' ? 0 : 1;
      if (aOpen !== bOpen) return aOpen - bOpen;
      const sw = (sevWeight[normSeverity(b.severity)] || 0) - (sevWeight[normSeverity(a.severity)] || 0);
      if (sw !== 0) return sw;
      return (b.created_at || '').localeCompare(a.created_at || '');
    });
  }, [remarks, filter]);

  const selectedRemark = useMemo(
    () => remarks.find((r) => r.id === selectedRemarkId) || null,
    [remarks, selectedRemarkId]
  );

  const handleChipClick = (kind: StatusFilter) => {
    setTab('remarks');
    if (kind === 'open') setFilter('open');
    else if (kind === 'e-open') setFilter('e');
    else if (kind === 's') setFilter('s');
    else if (kind === 'schedule') setTab('settings');
  };

  if (projectQ.isLoading) {
    return <FullPageSpinner />;
  }
  if (projectQ.isError || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="text-center">
          <h2 className="font-heading text-ink" style={{ fontSize: 24, fontWeight: 600 }}>Проект не найден</h2>
          <Button variant="ghost" onClick={() => nav('/projects')}>← К списку проектов</Button>
        </div>
      </div>
    );
  }

  // 13.4: тело списка замечаний (список + карточка). Переиспользуется как контент
  // вкладки (куратор/эксперт) и как содержимое окна-слоя (заказчик).
  const remarksBody = activeRole !== 'gip' ? (
    <>
      {isCustomer && promotableTotal > 0 && (
        <div className="rounded-card flex items-center" style={{ marginBottom: 16, padding: '12px 16px', gap: 12, background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.25)', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, fontSize: 13, minWidth: 200 }} className="text-ink">
            Движок нашёл <b>{promotableTotal}</b> {promotableTotal % 10 === 1 && promotableTotal % 100 !== 11 ? 'находку' : (promotableTotal % 10 >= 2 && promotableTotal % 10 <= 4 && (promotableTotal % 100 < 12 || promotableTotal % 100 > 14) ? 'находки' : 'находок')}.{' '}
            {(promotableQ.data?.hard_total ?? 0) > 0 && (
              <>Серьёзные (<b>{promotableQ.data!.hard_total}</b>) — отменённые нормы, пробелы состава, расхождения ТЭП — отмечены к передаче.{' '}</>
            )}
            {(promotableQ.data?.soft_total ?? 0) > 0 && (
              <>Спорные ({promotableQ.data!.soft_total}) можно добавить вручную в окне.</>
            )}
          </span>
          <Button variant="primary" size="sm" icon={<SparklesIcon size={15} />} onClick={() => setPromoteOpen(true)}>
            Сформировать замечания{(promotableQ.data?.hard_total ?? 0) > 0 ? ` (${promotableQ.data!.hard_total})` : ''}
          </Button>
        </div>
      )}
      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <SubFilterPills active={filter} onChange={setFilter} counts={counts} hideDefectFilters={isCustomer} showArchiveChip={!isCustomer} />
        {isCustomer ? (
          <div className="flex items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Button variant="secondary" size="sm" icon={<Plus size={16} />} onClick={() => setModal('create-remark')}>Добавить замечание</Button>
            <Button variant="secondary" size="sm" icon={<MessageCircleQuestion size={16} />} onClick={() => setAskSpecOpen(true)}>Спросить специалиста</Button>
          </div>
        ) : (
          <div className="flex items-center" style={{ gap: 8 }}>
            <Button variant="secondary" size="sm" icon={<Upload size={16} />} onClick={() => setTab('docs')}>Загрузить документы</Button>
            <Button variant="secondary" size="sm" icon={<SparklesIcon size={16} />} onClick={() => setModal('distribute-docs')}>Распределить</Button>
            <Button variant="secondary" size="sm" icon={<FileSearch size={16} />} onClick={() => { if (!activeSnapshot) { toast.error('Нет активного редакции'); return; } setModal('triage'); }}>Сортировка замечаний</Button>
            <Button variant="secondary" size="sm" icon={<FileUp size={16} />} onClick={() => setModal('word-import')}>Импорт из Word</Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-12" style={{ gap: 24 }}>
        <div className="col-span-5">
          {remarksQ.isLoading && <Spinner size={24} className="text-muted" />}
          {!remarksQ.isLoading && filtered.length === 0 && (
            <EmptyState
              icon={Inbox}
              title={filter === 'all' ? 'Замечаний пока нет' : 'Ничего не найдено по фильтру'}
              body={filter === 'all' ? 'Создайте первое замечание или импортируйте из Word' : undefined}
              cta={filter === 'all' ? <Button variant="primary" size="sm" onClick={() => setModal('create-remark')}>+ Создать</Button> : <Button variant="ghost" size="sm" onClick={() => setFilter('all')}>Сбросить</Button>}
            />
          )}
          {filtered.map((r) => (
            <RemarkCard
              key={r.id}
              remark={r}
              active={selectedRemarkId === r.id}
              role={activeRole}
              projectId={projectId}
              onClick={() => {
                track('remark_opened', {
                  project_id: projectId,
                  snapshot_id: r.snapshot_id,
                  target_entity_type: 'remark',
                  target_entity_id: r.id,
                  defect_axis: r.defect_type === 'E-gap' ? 'E' : r.defect_type === 'S-defect' ? 'S' : 'unknown',
                });
                setSelectedRemarkId(r.id);
              }}
            />
          ))}
        </div>
        <div className="col-span-7">
          {selectedRemark ? (
            <RemarkDetail remark={selectedRemark} projectId={projectId} role={activeRole} onClose={() => setSelectedRemarkId(null)} />
          ) : isCustomer ? (
            <EmptyState icon={Inbox} title="Выберите замечание слева" body="Нажмите на карточку, чтобы посмотреть детали и ответить." />
          ) : (
            <ProjectSummary summary={summary} projectId={projectId} />
          )}
        </div>
      </div>
    </>
  ) : null;

  return (
    <div className="min-h-screen bg-white">
      <DashboardTopBar
        projectId={projectId}
        projectCode={shortCode(project.name)}
        snapshotLabel={activeSnapshot ? formatSnapshotDate(activeSnapshot.created_at) : 'нет'}
        onOpenCommand={() => window.dispatchEvent(new CustomEvent('open-command-palette'))}
      />

      <ProjectHeader
        project={project}
        snapshotLabel={activeSnapshot ? new Date(activeSnapshot.created_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }) : 'нет'}
        version={snapshots.length}
        count={remarks.length}
        role={activeRole}
        aiSwap={aiSwap}
        onTitleClick={() => setPassportOpen(true)}
        onCreate={() => {
          // 13.4: у заказчика кнопка открывает окно замечаний (внутри — «Добавить»).
          // admin/curator — сразу создание; ГИП — импорт ответов.
          if (isCustomer) { openRemarksOverlay(); return; }
          setModal(activeRole === 'gip' ? 'response-import' : 'create-remark');
        }}
        onOpenAISummary={() => setAiPanelOpen((v) => !v)}
        onToggleAiSwap={() => setAiSwap((v) => !v)}
        onOpenDesigner={isCustomer ? () => setSettingsSection('designer') : undefined}
        onOpenDocs={isCustomer ? () => setDocsOpen(true) : undefined}
        onOpenSettings={isCustomer ? () => setSettingsSection('project') : undefined}
        onOpenConsultations={isCustomer ? () => setConsultOpen(true) : undefined}
      />

      <AIViewPanel open={aiPanelOpen} onClose={() => setAiPanelOpen(false)} role={activeRole} />

      <StatusBar
        staleDecisions={readinessQ.data?.decisions?.has_result ? (readinessQ.data.decisions.count ?? 0) : null}
        completenessGaps={readinessQ.data?.completeness?.has_result ? (readinessQ.data.completeness.absent ?? 0) : null}
        openCount={(summary?.open || 0) + (summary?.awaiting_response || 0)}
        deadline={project.deadline_at}
        onChipClick={handleChipClick}
        lastUpdated={remarksQ.dataUpdatedAt ? new Date(remarksQ.dataUpdatedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '—'}
        onRefresh={() => remarksQ.refetch()}
        slim={isCustomer}
      />

      {/* 13.5: у заказчика верхняя вкладка одна («Документация») — таб-бар не нужен,
          дом = сразу содержимое Документации (пилюли Загрузка/Готовность). */}
      {!aiSwap && !isCustomer && <TabBar active={tab} onChange={setTab} role={activeRole} />}
      {aiSwap && <AIFullSwapView role={activeRole} onClose={() => setAiSwap(false)} />}

      {!aiSwap && (
      <main className="mx-auto" style={{ maxWidth: 1280, paddingLeft: 24, paddingRight: 24, paddingTop: 24, paddingBottom: 48 }}>
        {tab === 'remarks' && activeRole === 'gip' && (
          <GIPSplitView projectId={projectId} deadlineAt={project.deadline_at} onGoToDocs={() => setTab('docs')} />
        )}

        {/* Куратор/эксперт: замечания — обычная вкладка. Заказчик: окно-слой (ниже). */}
        {tab === 'remarks' && !isCustomer && activeRole !== 'gip' && remarksBody}

        {tab === 'docs' && (
          isCustomer ? (
            // 14.1: пилюли «Загрузка/Готовность» убраны. «Загрузка и редакции»
            // переехали в чип шапки → окно (docsOpen). Дом = «Готовность».
            // Пустой проект (нет редакций) → сразу дропзона: загрузка документов —
            // первое действие заказчика, прятать её в окно на пустом проекте нельзя.
            snapshots.length === 0 ? (
              <DocumentsTab projectId={projectId} isCustomer />
            ) : (
              <IntegrityTab
                remarks={remarks}
                summary={summary}
                projectId={projectId}
                snapshotId={activeSnapshot?.id || null}
                snapshots={snapshots}
                onGoToRemarks={() => setTab('remarks')}
              />
            )
          ) : (
            <DocumentsTab projectId={projectId} />
          )
        )}
        {tab === 'query'     && <QueryTab projectId={projectId} snapshotId={activeSnapshot?.id || null} />}
        {tab === 'integrity' && (
          <IntegrityTab
            remarks={remarks}
            summary={summary}
            projectId={projectId}
            snapshotId={activeSnapshot?.id || null}
            snapshots={snapshots}
          />
        )}
        {tab === 'collegium' && <CollegiumTab projectId={projectId} snapshotId={activeSnapshot?.id || null} />}
        {tab === 'compare'   && <WavesView projectId={projectId} snapshots={snapshots} />}
        {tab === 'settings'  && <SettingsTab projectId={projectId} snapshots={snapshots} onGoToTab={setTab} />}
      </main>
      )}

      <PassportPanel
        open={passportOpen}
        projectId={projectId}
        onClose={() => setPassportOpen(false)}
      />

      {modal === 'create-remark' && (
        <CreateRemarkModal
          projectId={projectId}
          snapshotId={activeSnapshot?.id || null}
          onClose={() => setModal(null)}
        />
      )}
      {promoteOpen && (
        <PromoteFindingsModal projectId={projectId} onClose={() => setPromoteOpen(false)} onGoToRemarks={() => setTab('remarks')} />
      )}
      {askSpecOpen && (
        <AskSpecialistModal projectId={projectId} onClose={() => setAskSpecOpen(false)} />
      )}
      {consultOpen && (
        <ConsultationsListModal projectId={projectId} onClose={() => setConsultOpen(false)} />
      )}
      {settingsSection && (
        <ProjectSettingsSheet project={project} initialSection={settingsSection} onClose={closeSettings} />
      )}
      {/* 14.1: «Загрузка и редакции» — окно из чипа шапки (дропзона + тома + сравнение). */}
      {docsOpen && (
        <Modal title="Загрузка и редакции" onClose={() => setDocsOpen(false)} maxWidth={1120}>
          <DocumentsTab projectId={projectId} isCustomer />
          <div style={{ marginTop: 24 }}>
            <ComparePanel projectId={projectId} snapshots={snapshots} />
          </div>
        </Modal>
      )}
      {/* 13.4: окно-слой «Замечания» (заказчик). Широкий оверлей поверх страницы. */}
      {isCustomer && remarksOverlayOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 45, background: 'rgba(10,22,40,.35)', display: 'flex', justifyContent: 'center' }} onClick={closeRemarksOverlay}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', width: '100%', maxWidth: 1280, margin: '32px 16px', borderRadius: 16, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 64px)', overflow: 'hidden' }}
          >
            <div className="flex items-center justify-between" style={{ padding: '16px 24px', borderBottom: '1px solid #E5E7EB' }}>
              <h3 className="font-heading text-ink" style={{ fontSize: 18, fontWeight: 600 }}>Замечания приёмки</h3>
              <button type="button" onClick={closeRemarksOverlay} className="text-muted hover:text-ink" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }} aria-label="Закрыть">
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: 24, overflowY: 'auto' }}>
              {remarksBody}
            </div>
          </div>
        </div>
      )}
      {modal === 'create-project' && <CreateProjectModal onClose={() => setModal(null)} />}
      {modal === 'invite' && <InviteModal projectId={projectId} onClose={() => setModal(null)} />}
      {modal === 'word-import' && (
        <WordImportModal
          projectId={projectId}
          snapshotId={activeSnapshot?.id || null}
          onClose={() => setModal(null)}
        />
      )}
      {modal === 'response-import' && (
        <DocxResponseImportModal projectId={projectId} onClose={() => setModal(null)} />
      )}
      {modal === 'triage' && activeSnapshot && (
        <TriageModal projectId={projectId} snapshotId={activeSnapshot.id} onClose={() => setModal(null)} />
      )}
      {/* AutoRouting deprecated — см. комментарий у импорта. */}
      {modal === 'distribute-docs' && (
        <DistributeDocumentsModal
          projectId={projectId}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

function ProjectSummary({ summary, projectId }: { summary?: RemarksDashboardResponse['summary']; projectId: string }) {
  // Сводка по распределению (отдельный запрос — не блокирует основной)
  const assignQ = useQuery<{ summary: { total_documents: number; approved: number; pending_invite: number; needs_external: number; proposed: number } }>({
    queryKey: ['assignments-summary', projectId],
    queryFn: () => api.get(`/projects/${projectId}/assignments`),
    staleTime: 30_000,
    retry: false,
  });
  if (!summary) return null;
  const a = assignQ.data?.summary;
  return (
    <div className="bg-surface rounded-card" style={{ padding: 32, border: '1px solid #E5E7EB' }}>
      <h3 className="font-heading text-ink" style={{ fontSize: 20, fontWeight: 600 }}>Сводка по проекту</h3>
      <p className="text-muted mt-1" style={{ fontSize: 13 }}>Выберите замечание из списка слева для деталей</p>
      <div className="grid grid-cols-3 mt-6" style={{ gap: 16 }}>
        <Stat label="Всего замечаний" value={summary.total} />
        <Stat label="Открытых" value={summary.open + summary.awaiting_response} color={(summary.open + summary.awaiting_response) > 0 ? '#EF4444' : '#0A1628'} />
        <Stat label="Закрытых" value={summary.closed} color="#10B981" />
        <Stat label="Критических" value={summary.critical} color={summary.critical > 0 ? '#EF4444' : '#0A1628'} />
        <Stat label="Блокирующие" value={summary.s_defects} />
        <Stat label="Устранимые" value={summary.e_gaps} color={summary.e_gaps > 0 ? '#F59E0B' : '#0A1628'} />
      </div>
      {a && (a.total_documents > 0) && (
        <>
          <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', marginTop: 24 }}>
            Распределение документации
          </div>
          <div className="grid grid-cols-3" style={{ gap: 16, marginTop: 8 }}>
            <Stat label="Распределено" value={a.approved + a.proposed} color="#10B981" />
            <Stat label="Без аккаунта" value={a.pending_invite} color={a.pending_invite > 0 ? '#CA8A04' : '#0A1628'} />
            <Stat label="Нет в команде" value={a.needs_external} color={a.needs_external > 0 ? '#EF4444' : '#0A1628'} />
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, color = '#0A1628' }: { label: string; value: number; color?: string }) {
  return (
    <div className="bg-white rounded-card border border-border" style={{ padding: 16 }}>
      <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</div>
      <div className="font-heading tabular mt-1" style={{ fontSize: 28, fontWeight: 600, color }}>{value}</div>
    </div>
  );
}

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Spinner size={32} className="text-muted" />
    </div>
  );
}

// (PlaceholderTab удалён — все табы реализованы в Sprint 4)

function shortCode(name: string): string {
  const m = name.match(/^[А-ЯA-Z]{2,5}[-\s]?\d{1,6}/);
  return m ? m[0] : name.split(/[—\-]/)[0].trim().slice(0, 12) || 'Проект';
}
function formatSnapshotDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso.slice(0, 10); }
}
